<!-- The Analyse board (styles.css patterns: dash-seg, dash-icon-btn, dash-overlay / dash-sheet, dash-list / dash-row) -->
<div class="bd" [class.bd-ro]="readOnly">

  <!-- head: back to the dashboard (desktop; phones have the tab bar), save state, snapshot link, PDF -->
  <div class="bd-top">
    <button type="button" class="btn btn-sm btn-outline-secondary d-none d-md-inline-flex align-items-center gap-1"
            (click)="goBack()">← {{ 'BOARD_BACK' | translate }}</button>
    <span class="bd-kicker">📌 {{ 'TAB_ANALYSE' | translate }}</span>
    <span class="bd-top-actions">
      <!-- every edit of a slot is saved on the server -->
      <ng-container *ngIf="board.mode === 'slot'" [ngSwitch]="board.saveState">
        <span *ngSwitchCase="'saving'" class="bd-save text-muted" role="status">{{ 'BOARD_SAVING' | translate }}</span>
        <span *ngSwitchCase="'saved'" class="bd-save bd-save-ok" role="status">{{ 'BOARD_SAVED' | translate }}</span>
        <span *ngSwitchCase="'error'" class="bd-save text-danger" role="alert">
          ⚠ <span class="d-none d-sm-inline">{{ 'BOARD_SAVE_FAILED' | translate }}</span>
          <button type="button" class="btn btn-link btn-sm p-0 align-baseline bd-save-retry" (click)="board.retrySave()">{{ 'BOARD_SAVE_RETRY' | translate }}</button>
        </span>
      </ng-container>
      <ng-container *ngIf="state">
        <!-- 🔗 the read-only snapshot of what is on screen (never the slot's own URL) -->
        <button type="button" class="btn btn-sm btn-outline-secondary m-touch-sm" (click)="copyLink()"
                [title]="(copied ? 'LINK_COPIED' : 'BOARD_LINK_HINT') | translate" [attr.aria-label]="'BOARD_LINK_HINT' | translate">{{ copied ? '✓' : '🔗' }}</button>
        <button type="button" class="btn btn-sm btn-outline-secondary m-touch-sm d-inline-flex align-items-center gap-1"
                [disabled]="pdfBusy || !views.length" (click)="exportPdf()"
                [title]="'BOARD_PDF_HINT' | translate" [attr.aria-label]="'BOARD_PDF_HINT' | translate">
          <span *ngIf="pdfBusy" class="spinner-border spinner-border-sm" role="status"></span>
          <span *ngIf="!pdfBusy" aria-hidden="true">📄</span> PDF
        </button>
      </ng-container>
    </span>
  </div>

  <!-- slot bar (admin): one chip per saved analysis, ＋ a new one, ⋯ the active one's actions -->
  <div *ngIf="board.admin && (board.slots.length || board.mode === 'slot' || board.mode === 'snapshot' || board.mode === 'missing')" class="bd-slots">
    <div class="bd-slot-strip" role="list" [attr.aria-label]="'BOARD_SLOTS' | translate">
      <button *ngFor="let s of board.slots; let i = index; trackBy: trackSlot" type="button" role="listitem"
              class="bd-slot" [class.bd-slot-on]="s.id === board.activeSlotId"
              [attr.aria-current]="s.id === board.activeSlotId ? 'page' : null"
              [title]="slotTitle(s)" (click)="openSlot(s)" (dblclick)="s.id === board.activeSlotId && renameSlot()">
        <span class="bd-slot-n">{{ i + 1 }}</span><span class="bd-slot-t">{{ slotTitle(s) }}</span>
      </button>
      <button type="button" class="bd-slot bd-slot-new" [disabled]="board.busy || board.slots.length >= limits.slots"
              (click)="newSlot()" [title]="'BOARD_SLOT_NEW' | translate" [attr.aria-label]="'BOARD_SLOT_NEW' | translate">＋</button>
    </div>
    <div *ngIf="board.mode === 'slot'" #menuEl class="bd-menu-wrap">
      <button type="button" class="btn btn-sm btn-outline-secondary bd-icon" [attr.aria-expanded]="menuOpen" aria-haspopup="menu"
              (click)="toggleMenu($event)" [title]="'BOARD_SLOT_ACTIONS' | translate" [attr.aria-label]="'BOARD_SLOT_ACTIONS' | translate">⋯</button>
      <div *ngIf="menuOpen" class="card shadow bd-menu" role="menu" (click)="$event.stopPropagation()">
        <button type="button" class="bd-menu-item" role="menuitem" (click)="renameSlot()">✏️ {{ 'BOARD_SLOT_RENAME' | translate }}</button>
        <button type="button" class="bd-menu-item" role="menuitem" [disabled]="board.busy || board.slots.length >= limits.slots"
                (click)="duplicateSlot()">⧉ {{ 'BOARD_SLOT_DUPLICATE' | translate }}</button>
        <div class="bd-menu-row" role="group" [attr.aria-label]="'BOARD_SLOT_ORDER' | translate">
          <span class="bd-menu-lbl">{{ 'BOARD_SLOT_ORDER' | translate }}</span>
          <button type="button" class="btn btn-sm btn-outline-secondary bd-icon" role="menuitem" [disabled]="board.busy || slotIndex <= 0"
                  (click)="moveSlot(-1)" [title]="'BOARD_SLOT_EARLIER' | translate" [attr.aria-label]="'BOARD_SLOT_EARLIER' | translate">←</button>
          <button type="button" class="btn btn-sm btn-outline-secondary bd-icon" role="menuitem"
                  [disabled]="board.busy || slotIndex < 0 || slotIndex >= board.slots.length - 1"
                  (click)="moveSlot(1)" [title]="'BOARD_SLOT_LATER' | translate" [attr.aria-label]="'BOARD_SLOT_LATER' | translate">→</button>
        </div>
        <button *ngIf="!confirmDelete" type="button" class="bd-menu-item text-danger" role="menuitem" [disabled]="board.busy"
                (click)="deleteSlot()">🗑 {{ 'BOARD_SLOT_DELETE' | translate }}</button>
        <div *ngIf="confirmDelete" class="bd-menu-confirm" role="alert">
          <div class="small mb-2">{{ 'BOARD_SLOT_DELETE_CONFIRM' | translate: { title: board.activeTitle } }}</div>
          <button type="button" class="btn btn-sm btn-danger me-1" (click)="deleteSlot()">{{ 'BOARD_SLOT_DELETE' | translate }}</button>
          <button type="button" class="btn btn-sm btn-outline-secondary" (click)="closeMenu()">{{ 'BOARD_CANCEL' | translate }}</button>
        </div>
      </div>
    </div>
  </div>
  <div *ngIf="board.actionError" class="alert alert-danger py-2 px-3 bd-alert" role="alert">{{ board.actionError | translate: { max: limits.slots } }}</div>

  <!-- a snapshot link: read-only for everybody; an admin may keep it as a slot -->
  <div *ngIf="board.mode === 'snapshot'" class="bd-snap" role="note">
    <span class="bd-snap-icon" aria-hidden="true">🔒</span>
    <span class="bd-snap-text"><strong>{{ 'BOARD_SNAPSHOT' | translate }}</strong> · {{ 'BOARD_SNAPSHOT_HINT' | translate }}</span>
    <button *ngIf="board.admin" type="button" class="btn btn-sm btn-primary m-touch-sm bd-snap-save" [disabled]="board.busy"
            (click)="saveSnapshot()">{{ 'BOARD_SNAPSHOT_SAVE' | translate }}</button>
  </div>

  <!-- PDF on a phone whose share sheet needs a fresh tap -->
  <div *ngIf="pdfReady" class="alert alert-success d-flex align-items-center flex-wrap gap-2 py-2 px-3 bd-alert" role="status">
    <span class="me-auto">📄 {{ 'BOARD_PDF_READY' | translate }}</span>
    <button type="button" class="btn btn-sm btn-success m-touch-sm" (click)="sharePdf()">{{ 'BOARD_PDF_SHARE' | translate }}</button>
    <button type="button" class="btn btn-sm btn-outline-success m-touch-sm" (click)="downloadPdf()">{{ 'BOARD_PDF_DOWNLOAD' | translate }}</button>
  </div>
  <div *ngIf="pdfFailed" class="alert alert-danger py-2 px-3 bd-alert" role="alert">{{ 'BOARD_PDF_FAILED' | translate }}</div>

  <!-- no board to show: loading, a note for guests, the admin's first slot, a missing slot, a failed list -->
  <ng-container *ngIf="!state" [ngSwitch]="board.mode">
    <div *ngSwitchCase="'loading'" class="bd-loading text-muted" role="status">
      <span class="spinner-border spinner-border-sm me-2"></span>{{ 'LADE' | translate }}
    </div>
    <div *ngSwitchCase="'noSlots'" class="card bd-empty">
      <div class="card-body text-center">
        <div class="bd-empty-icon" aria-hidden="true">📌</div>
        <div class="fw-semibold mb-1">{{ 'BOARD_NO_SLOTS' | translate }}</div>
        <p class="text-muted small mb-3">{{ 'BOARD_NO_SLOTS_HINT' | translate }}</p>
        <button type="button" class="btn btn-primary m-touch" [disabled]="board.busy" (click)="newSlot()">＋ {{ 'BOARD_SLOT_NEW' | translate }}</button>
      </div>
    </div>
    <div *ngSwitchCase="'missing'" class="card bd-empty">
      <div class="card-body text-center">
        <div class="bd-empty-icon" aria-hidden="true">❔</div>
        <div class="fw-semibold mb-1">{{ 'BOARD_SLOT_MISSING' | translate }}</div>
        <p class="text-muted small mb-0">{{ 'BOARD_SLOT_MISSING_HINT' | translate }}</p>
      </div>
    </div>
    <div *ngSwitchCase="'failed'" class="card bd-empty">
      <div class="card-body text-center">
        <div class="bd-empty-icon" aria-hidden="true">⚠️</div>
        <div class="fw-semibold mb-3">{{ 'BOARD_SLOTS_FAILED' | translate }}</div>
        <button type="button" class="btn btn-outline-primary m-touch" (click)="board.loadSlots()">{{ 'BOARD_SAVE_RETRY' | translate }}</button>
      </div>
    </div>
    <div *ngSwitchCase="'guest'" class="card bd-empty">
      <div class="card-body text-center">
        <div class="bd-empty-icon" aria-hidden="true">📌</div>
        <div class="fw-semibold mb-1">{{ 'BOARD_GUEST' | translate }}</div>
        <p class="text-muted small mb-1">{{ 'BOARD_GUEST_HINT' | translate }}</p>
        <p *ngIf="board.linkedSlot" class="text-muted small mb-1">{{ 'BOARD_GUEST_SLOT' | translate }}</p>
        <button type="button" class="btn btn-sm btn-outline-secondary m-touch-sm mt-2" (click)="login.emit()">⚙️ {{ 'BOARD_GUEST_LOGIN' | translate }}</button>
      </div>
    </div>
  </ng-container>

  <ng-container *ngIf="state">
    <!-- what the problem is: both grow with their text (a phone shows all of it); a snapshot shows them as text -->
    <ng-container *ngIf="!readOnly">
      <textarea #titleEl class="form-control bd-title" rows="1" [maxlength]="limits.title" enterkeyhint="done"
                [ngModel]="title" (ngModelChange)="onTitle($event)" (input)="grow(titleEl)"
                (keydown.enter)="$event.preventDefault(); titleEl.blur()"
                [placeholder]="'BOARD_TITLE_PLACEHOLDER' | translate" [attr.aria-label]="'BOARD_TITLE_LABEL' | translate"></textarea>
      <textarea #noteEl class="form-control bd-note" rows="2" [maxlength]="limits.note"
                [ngModel]="note" (ngModelChange)="onNote($event)" (input)="grow(noteEl)"
                [placeholder]="'BOARD_NOTE_PLACEHOLDER' | translate" [attr.aria-label]="'BOARD_NOTE_LABEL' | translate"></textarea>
    </ng-container>
    <ng-container *ngIf="readOnly">
      <h1 class="bd-title-ro">{{ title.trim() || ('TAB_ANALYSE' | translate) }}</h1>
      <p *ngIf="note.trim()" class="bd-note-ro">{{ note }}</p>
    </ng-container>

    <!-- one time window for all charts (slots; a snapshot's is fixed: the line below says which) -->
    <div *ngIf="!readOnly" class="bd-timebar" role="group" [attr.aria-label]="'BOARD_WINDOW' | translate">
      <div class="btn-group btn-group-sm dash-seg bd-presets" role="group">
        <button *ngFor="let p of presets" type="button" class="btn"
                [ngClass]="activePreset === p.key ? 'btn-secondary' : 'btn-outline-secondary'"
                [attr.aria-pressed]="activePreset === p.key"
                [title]="'BOARD_PRESET_HINT' | translate" (click)="preset(p.key)">{{ p.label | translate }}</button>
      </div>
      <!-- desktop: Von [ ] Bis [ ] ⟲ jetzt in one row; phones: "Von [ ] ⟲" / "Bis [ ] jetzt" -->
      <div class="bd-range">
        <label class="bd-lbl bd-lbl-from" for="bdFrom">{{ 'BOARD_FROM' | translate }}</label>
        <!-- applied on Enter, on leaving the field or after a pause — not at every segment typed -->
        <input id="bdFrom" class="form-control form-control-sm bd-in-from" type="datetime-local" [class.is-invalid]="!!rangeError"
               [ngModel]="fromText" (ngModelChange)="fromText = $event; onRangeEdit()"
               (blur)="applyRange()" (keydown.enter)="applyRange()">
        <button type="button" class="btn btn-sm btn-outline-secondary bd-icon bd-undo" [disabled]="!canUndo"
                (click)="stepBack()" [title]="'BOARD_UNDO' | translate" [attr.aria-label]="'BOARD_UNDO' | translate">⟲</button>
        <label class="bd-lbl bd-lbl-to" for="bdTo">{{ 'BOARD_TO' | translate }}</label>
        <input id="bdTo" class="form-control form-control-sm bd-in-to" type="datetime-local" [class.is-invalid]="!!rangeError"
               [ngModel]="toText" (ngModelChange)="toText = $event; onRangeEdit()"
               (blur)="applyRange()" (keydown.enter)="applyRange()">
        <button type="button" class="btn btn-sm btn-outline-secondary bd-now m-touch-sm" (click)="toNow()"
                [title]="'BOARD_NOW_HINT' | translate">{{ 'BOARD_NOW' | translate }}</button>
      </div>
    </div>
    <div class="bd-window dash-meta">
      <span *ngIf="loadingCount" class="spinner-border bd-spin text-muted" role="status" [attr.aria-label]="'LADE' | translate"></span>
      <span *ngIf="readOnly" aria-hidden="true">🕑 </span>
      <span class="fw-semibold">{{ windowLabel }}</span>
      <span *ngIf="rolling" class="text-muted"> · {{ 'BOARD_ROLLING' | translate }}</span>
      <span *ngIf="rangeError" class="text-danger"> · {{ rangeError | translate }}</span>
      <span *ngIf="views.length && !readOnly" class="text-muted"> · {{ (phone ? 'BOARD_ZOOM_HINT_TOUCH' : 'BOARD_ZOOM_HINT') | translate }}</span>
    </div>

    <!-- empty board -->
    <div *ngIf="!views.length" class="card bd-empty">
      <div class="card-body text-center">
        <div class="bd-empty-icon" aria-hidden="true">📌</div>
        <ng-container *ngIf="!readOnly">
          <div class="fw-semibold mb-1">{{ 'BOARD_EMPTY' | translate }}</div>
          <p class="text-muted small mb-3">{{ 'BOARD_EMPTY_HINT' | translate }}</p>
          <button type="button" class="btn btn-primary m-touch" (click)="openPicker()">➕ {{ 'BOARD_ADD_CHART' | translate }}</button>
        </ng-container>
        <div *ngIf="readOnly" class="fw-semibold">{{ 'BOARD_SNAPSHOT_EMPTY' | translate }}</div>
      </div>
    </div>

    <!-- the charts -->
    <div *ngIf="views.length" class="bd-grid" [class.bd-two]="views.length > 1">
      <section *ngFor="let v of views; let i = index; let first = first; let last = last; trackBy: trackView" class="card bd-card"
               [class.bd-wide]="wideCard(i)" [attr.aria-label]="v.title">
        <div class="bd-card-head">
          <h2 class="bd-card-title"><span aria-hidden="true">{{ v.icon }}</span> {{ v.title }}</h2>
          <ng-container *ngIf="!readOnly">
            <button type="button" class="btn btn-sm btn-outline-secondary bd-icon" [disabled]="first" (click)="move(v, -1)"
                    [title]="'BOARD_MOVE_UP' | translate" [attr.aria-label]="'BOARD_MOVE_UP' | translate">↑</button>
            <button type="button" class="btn btn-sm btn-outline-secondary bd-icon" [disabled]="last" (click)="move(v, 1)"
                    [title]="'BOARD_MOVE_DOWN' | translate" [attr.aria-label]="'BOARD_MOVE_DOWN' | translate">↓</button>
            <button type="button" class="btn btn-sm btn-outline-secondary bd-icon" (click)="remove(v)"
                    [title]="'BOARD_REMOVE_CHART' | translate" [attr.aria-label]="'BOARD_REMOVE_CHART' | translate">✕</button>
          </ng-container>
        </div>
        <div *ngIf="!isSettings(v)" class="bd-chart" [style.height.px]="v.config?.height ?? null">
          <app-board-chart *ngIf="v.config" [config]="v.config" [label]="v.title"></app-board-chart>
          <div *ngIf="!v.ref" class="bd-chart-state">{{ 'BOARD_UNKNOWN_CHART' | translate }}</div>
          <div *ngIf="v.ref && !v.config && v.loading" class="bd-chart-state">
            <span class="spinner-border spinner-border-sm text-muted" role="status"></span>
          </div>
          <div *ngIf="v.ref && v.failed && !v.loading" class="bd-chart-state text-danger">{{ 'BOARD_LOAD_FAILED' | translate }}</div>
          <div *ngIf="v.config && !v.config.points && !v.loading && !v.failed" class="bd-chart-state">
            <span class="bd-chart-badge">{{ 'BOARD_NO_DATA' | translate }}</span>
          </div>
        </div>
        <!-- min · Ø · max over every raw point; the extremes' times as tooltips, or inline after a tap (phones) -->
        <div *ngIf="v.stats.length" class="bd-stats" [class.bd-stats-tap]="hasStatTimes" (click)="toggleStatTimes()"
             [attr.role]="hasStatTimes ? 'button' : null" [attr.tabindex]="hasStatTimes ? 0 : null"
             [attr.aria-pressed]="hasStatTimes ? statTimes : null" (keydown.enter)="toggleStatTimes()"
             [attr.aria-label]="hasStatTimes ? ('BOARD_STAT_TIMES_HINT' | translate) : null">
          <span *ngFor="let s of v.stats" class="bd-stat">
            <i class="bd-swatch" [style.border-top-color]="s.color" [class.bd-swatch-dash]="s.dash.length" aria-hidden="true"></i>{{ s.label }}: <span class="bd-stat-val"><span *ngIf="s.approx" class="bd-stat-approx">≈ </span><ng-container *ngFor="let p of s.parts; let last = last"><span [attr.title]="p.title || null">{{ p.text }}<span *ngIf="statTimes && p.at" class="bd-stat-at"> ({{ p.at }})</span></span><ng-container *ngIf="!last"> · </ng-container></ng-container></span>
          </span>
        </div>
        <!-- 'froeling.settings' (no chart, board-settings.ts): the live settings by component, the neutral hints and
             the change log over the window; a snapshot link carries no settings (it says so) -->
        <div *ngIf="isSettings(v)" class="bs">
          <ng-container *ngIf="settingsOf(v) as st; else settingsWait">
            <div class="bs-meta">{{ 'FS_BOARD_STAND' | translate: { time: st.view.stand } }}<ng-container *ngIf="readOnly"> ·
              <span class="bs-snap">{{ 'FS_BOARD_SNAPSHOT' | translate }}</span></ng-container></div>
            <div *ngIf="st.view.hints.length" class="bs-hints">
              <div class="bs-hints-title">{{ 'FS_HINTS_TITLE' | translate }}</div>
              <ul><li *ngFor="let h of st.view.hints">{{ h }}</li></ul>
            </div>
            <div class="bs-groups">
              <div *ngFor="let g of st.view.groups; trackBy: trackSettingsGroup" class="bs-group">
                <h3 class="bs-group-title" [title]="g.sub || g.title"><span aria-hidden="true">{{ g.icon }}</span> {{ g.title }}<span
                    *ngIf="g.sub" class="bs-group-sub"> · {{ g.sub }}</span></h3>
                <div *ngFor="let r of g.rows" class="bs-row" [title]="r.title">
                  <span class="bs-l">{{ r.label }}</span><span class="bs-v">{{ r.value }}</span>
                </div>
              </div>
            </div>
            <div class="bs-log">
              <h3 class="bs-sec">📝 {{ 'FS_BOARD_CHANGES' | translate }}<span *ngIf="st.view.changes.length" class="bs-count">{{ st.view.changes.length }}</span></h3>
              <div *ngIf="st.view.changes.length" class="bs-changes" role="table" [attr.aria-label]="'FS_BOARD_CHANGES' | translate">
                <div class="bs-ch bs-ch-head" role="row">
                  <span role="columnheader">{{ 'FS_BOARD_COL_TIME' | translate }}</span>
                  <span role="columnheader">{{ 'FS_BOARD_COL_COMPONENT' | translate }}</span>
                  <span role="columnheader">{{ 'FS_BOARD_COL_SETTING' | translate }}</span>
                  <span role="columnheader">{{ 'FS_BOARD_COL_CHANGE' | translate }}</span>
                </div>
                <div *ngFor="let c of st.view.changes" class="bs-ch" role="row">
                  <span role="cell" class="bs-ch-time">{{ c.time }}</span>
                  <span role="cell" class="bs-ch-comp">{{ c.component }}</span>
                  <span role="cell" class="bs-ch-set">{{ c.setting }}</span>
                  <span role="cell" class="bs-ch-val"><span class="bs-old">{{ c.from }}</span> → <strong>{{ c.to }}</strong></span>
                </div>
              </div>
              <div *ngIf="st.note" class="bs-note">{{ st.note }}</div>
            </div>
          </ng-container>
          <ng-template #settingsWait>
            <div class="bs-meta"><span class="spinner-border text-muted bd-spin" role="status"></span>{{ 'FS_BOARD_WAIT' | translate }}</div>
          </ng-template>
        </div>
        <!-- 'valves.all': the evaluation per room over the window (/api/history/stats); a header click sorts (slots) -->
        <div *ngIf="isValvesAll(v)" class="bv">
          <div class="bv-head">
            <h3 class="bv-title">{{ 'BOARD_VALVES_EVAL' | translate }}</h3>
            <span *ngIf="!v.valveEval" class="spinner-border text-muted bd-spin" role="status" [attr.aria-label]="'LADE' | translate"></span>
            <span *ngIf="v.valveEval?.unavailable" class="bv-hint">{{ 'BOARD_VALVES_EVAL_UNAVAILABLE' | translate }}</span>
          </div>
          <table *ngIf="v.valveEval && v.valveEval.rows.length && !v.valveEval.unavailable" class="bv-table">
            <colgroup><col class="bv-c-room"><col span="4" class="bv-c-num"></colgroup>
            <thead>
              <tr>
                <th scope="col"><button type="button" class="bv-sort" [disabled]="readOnly" (click)="sortValves(v, 'floor')"
                    [title]="readOnly ? '' : ('BOARD_VALVES_SORT_HINT' | translate)">{{ 'BOARD_VALVES_COL_ROOM' | translate }}</button></th>
                <th scope="col" class="bv-num" [attr.aria-sort]="valveSortAria(v, 'mean')"><button type="button" class="bv-sort" [disabled]="readOnly"
                    (click)="sortValves(v, 'mean')" [title]="'BOARD_VALVES_COL_MEAN_HINT' | translate">{{ 'BOARD_VALVES_COL_MEAN' | translate }}{{ valveArrow(v, 'mean') }}</button></th>
                <th scope="col" class="bv-num" [attr.aria-sort]="valveSortAria(v, 'open')"><button type="button" class="bv-sort" [disabled]="readOnly"
                    (click)="sortValves(v, 'open')" [title]="'BOARD_VALVES_COL_OPEN_HINT' | translate">{{ 'BOARD_VALVES_COL_OPEN' | translate }}{{ valveArrow(v, 'open') }}</button></th>
                <th scope="col" class="bv-num" [attr.aria-sort]="valveSortAria(v, 'closed')"><button type="button" class="bv-sort" [disabled]="readOnly"
                    (click)="sortValves(v, 'closed')" [title]="'BOARD_VALVES_COL_CLOSED_HINT' | translate">{{ 'BOARD_VALVES_COL_CLOSED' | translate }}{{ valveArrow(v, 'closed') }}</button></th>
                <th scope="col" class="bv-num" [attr.aria-sort]="valveSortAria(v, 'dt')"><button type="button" class="bv-sort" [disabled]="readOnly"
                    (click)="sortValves(v, 'dt')" [title]="'BOARD_VALVES_COL_DT_HINT' | translate">{{ 'BOARD_VALVES_COL_DT' | translate }}{{ valveArrow(v, 'dt') }}</button></th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let r of valveRows(v); trackBy: trackValve" [class.bv-floor]="r.floorStart">
                <th scope="row" class="bv-room" [attr.title]="r.likely ? ('BOARD_VALVES_LIKELY_NOTE' | translate) : r.label">{{ r.label }}</th>
                <ng-container *ngIf="r.hasValve; else noValve">
                  <td class="bv-num"><span class="bv-val">{{ r.mean }}</span><span class="bv-bar"><i [style.width.%]="r.meanW"></i></span></td>
                  <td class="bv-num"><span class="bv-val">{{ r.open }}</span><span class="bv-bar bv-bar-open"><i [style.width.%]="r.openW"></i></span></td>
                  <td class="bv-num"><span class="bv-val">{{ r.closed }}</span><span class="bv-bar bv-bar-closed"><i [style.width.%]="r.closedW"></i></span></td>
                </ng-container>
                <ng-template #noValve><td colspan="3" class="bv-none">{{ 'BOARD_VALVES_NONE' | translate }}</td></ng-template>
                <td class="bv-num"><span class="bv-val">{{ r.dt }}</span><span class="bv-bar bv-bar-dt"><i
                    [style.left.%]="r.dtLeft" [style.width.%]="r.dtW" [class.bv-cold]="r.dtCold"></i></span></td>
              </tr>
            </tbody>
          </table>
          <div *ngIf="v.valveEval && v.valveEval.rows.length && !v.valveEval.unavailable" class="bv-foot">
            {{ 'BOARD_VALVES_FOOT' | translate }}<ng-container *ngIf="valveHasLikely(v)"> · {{ 'BOARD_VALVES_LIKELY_NOTE' | translate }}</ng-container>
          </div>
        </div>
      </section>
    </div>

    <button *ngIf="views.length && !readOnly" type="button" class="btn btn-outline-primary m-touch bd-add" (click)="openPicker()">
      ➕ {{ 'BOARD_ADD_CHART' | translate }}
    </button>
  </ng-container>
</div>

<!-- Catalogue picker: a centred card from 576 px, a full-screen sheet on phones; a tap adds / removes a chart -->
<div *ngIf="pickerOpen" class="dash-overlay dash-overlay-pad" style="background:rgba(0,0,0,0.45);" (click)="closePicker()">
  <div class="card shadow-lg border dash-sheet bd-picker" style="width:100%;max-width:620px;max-height:calc(100vh - 48px);"
       role="dialog" aria-modal="true" [attr.aria-label]="'BOARD_ADD_CHART' | translate" (click)="$event.stopPropagation()">
    <div class="dash-sheet-head dash-sheet-head-plain">
      <div class="dash-sheet-title">➕ {{ 'BOARD_ADD_CHART' | translate }}</div>
      <span class="badge rounded-pill text-bg-secondary">{{ views.length }}</span>
      <button type="button" class="btn btn-sm btn-primary" (click)="closePicker()">{{ 'BOARD_PICKER_DONE' | translate }}</button>
    </div>
    <div class="px-3 pb-2 bd-picker-search">
      <input class="form-control form-control-sm" type="search" [(ngModel)]="pickerFilter"
             [placeholder]="'SUCHEN' | translate" [attr.aria-label]="'SUCHEN' | translate">
      <div *ngIf="pickerFull" class="small text-muted mt-1">{{ 'BOARD_PICKER_FULL' | translate: { max: limits.charts } }}</div>
    </div>
    <div class="dash-sheet-body bd-picker-body">
      <ng-container *ngFor="let g of filteredCatalog; trackBy: trackGroup">
        <div class="bd-pick-group">{{ g.title }}</div>
        <div *ngIf="groupEmpty(g)" class="text-muted small px-3 pb-2">{{ (g.key === 'rooms' ? 'BOARD_ROOMS_WAIT' : 'BOARD_GROUP_WAIT') | translate }}</div>
        <div *ngIf="!groupEmpty(g)" class="card dash-list mb-2" [class.dash-list-cols]="g.key === 'rooms' && !phone">
          <ng-container *ngFor="let sg of g.subgroups; trackBy: trackSub">
            <div *ngIf="sg.title" class="dash-list-group">{{ sg.title }}</div>
            <button *ngFor="let it of sg.items; trackBy: trackItem" type="button" class="dash-row bd-pick-row"
                    [class.bd-picked]="board.has(it.id)" [attr.aria-pressed]="board.has(it.id)"
                    [disabled]="pickerFull && !board.has(it.id)" (click)="board.toggle(it.id)">
              <span class="dash-row-icon" aria-hidden="true">{{ it.icon }}</span>
              <span class="dash-row-main"><span class="dash-row-title d-block">{{ it.title }}</span></span>
              <span class="dash-row-end"><span class="bd-check" aria-hidden="true">{{ board.has(it.id) ? '✓' : '+' }}</span></span>
            </button>
          </ng-container>
        </div>
      </ng-container>
      <div *ngIf="!filteredCatalog.length" class="text-muted small text-center py-3">{{ 'KEINE_EINTRAEGE' | translate }}</div>
    </div>
  </div>
</div>
